import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { AppSettings } from '../app-settings';
import { BehaviorSubject, of, throwError } from 'rxjs';
import { CommonService } from './common.service';
import { formatDate } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class BlockedSeatsService {

  // Save Blocked Map
  private blockedSaveAction = new BehaviorSubject<any>(null);
  public blockedSaveObservable = this.blockedSaveAction.asObservable();

  constructor(private http: HttpClient, private common: CommonService) { }

  saveBlockedMapAction(save: any) {
    this.blockedSaveAction.next(save);
  }

  getBlockedSeatsBySpace(idSpace: number) {
    const url = `${AppSettings.API_URI_V2}/blocked-seats/space/${idSpace}`;
    return this.http.get<any>(url).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  /**
   * Grupos de bloqueo propios del recinto (PMR, cabinas, butacas retiradas...)
   * en la versión vigente de su mapa, con nombre, color y butacas. Es lo que
   * necesita el mapa de butacas de los abonos para pintar cada bloqueo con su
   * color y poder nombrarlo, ya que ahí no hay evento ni sesión.
   *
   * El endpoint devuelve todos los bloqueos guardados sobre el espacio, y los de
   * los eventos que se celebran ahí también llevan `id_space`, así que se filtran
   * los que cuelgan de un evento o una sesión: no tienen nada que ver con el
   * mapa del abono. Si falla, se devuelve lista vacía: sin nombres ni colores el
   * mapa sigue funcionando (los bloqueos llegan aparte, ya resueltos).
   */
  getSpaceBlockedGroups(idSpace: number) {
    return this.getBlockedSeatsBySpace(idSpace).pipe(
      map((res: any) => {
        const groups: any[] = Array.isArray(res?.data) ? res.data : (res?.data?.blocked_seats ?? []);
        return groups.filter((group: any) => !group?.id_event && !group?.id_session);
      }),
      catchError(() => of([]))
    );
  }

  getBlockedSeatsByEvent(idEvent: number) {
    const url = `${AppSettings.API_URI_V2}/blocked-seats/event/${idEvent}`;
    return this.http.get<any>(url).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  getBlockedSeatsBySession(idEvent: number, idSession: number) {
    const url = `${AppSettings.API_URI_V2}/blocked-seats/event/${idEvent}/session/${idSession}`;
    return this.http.get<any>(url).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  createBlockedSeatsBySpace(idSpace: number, payload: any) {
    const url = `${AppSettings.API_URI_V2}/blocked-seats/space/${idSpace}`;
    return this.http.post<any>(url, payload).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  createBlockedSeatsByEvent(idEvent: number, payload: any) {
    const url = `${AppSettings.API_URI_V2}/blocked-seats/event/${idEvent}`;
    return this.http.post<any>(url, payload).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  createBlockedSeatsBySession(idEvent: number, idSession: number, payload: any) {
    const url = `${AppSettings.API_URI_V2}/blocked-seats/event/${idEvent}/session/${idSession}`;
    return this.http.post<any>(url, payload).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  createGeneralBlocked(payload: any) {
    const url = `${AppSettings.API_URI_V2}/blocked-seats/new`;
    return this.http.post<any>(url, payload).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  editBlocked(blockedId: number, payload: any) {
    const url = `${AppSettings.API_URI_V2}/blocked-seats/${blockedId}`;
    return this.http.put<any>(url, payload).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  deleteBlocked(blockedId: number) {
    const url = `${AppSettings.API_URI_V2}/blocked-seats/${blockedId}`;
    return this.http.delete<any>(url).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  
  getCountTicketsByEvent(idSession: number) {
    const url = `${AppSettings.API_URI_V2}/count/tickets/enabled/session/${idSession}`;
    return this.http.get(url).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

  getSessionBlockedSeatGroups(idSession: number) {
    const url = `${AppSettings.API_URI_V2}/sessions/${idSession}/blocked-seat-groups`;
    return this.http.get<any>(url).pipe(
      map(data => data),
      catchError(err => { return throwError(() => err); })
    );
  }

  unlockMultipleBlockedSeats(idSession: number, ids: number[]) {
    const url = `${AppSettings.API_URI_V2}/sessions/${idSession}/blocked-seats-unlock`;
    return this.http.post<any>(url, { ids }).pipe(
      map(data => data),
      catchError(err => { return throwError(() => err); })
    );
  }

  getMapEventBySession(idEvent: number, idSession: number) {
    const url = `${AppSettings.API_URI_V2}/events/${idEvent}/sessions/${idSession}/map`;
    return this.http.get(url).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

}
